import { mapAggregate, objectBy } from '../../../util/index.js';
import { yieldTask } from '../../../util/schedule.js';
import { FixedDistributionDateBehavior, } from '../../fixed-distributions/index.js';
import { caseBillingIsActive, findAndSort, forceCollectionCommissionBilling, genResidueVouchers, getBaseBillingVoucher, getCollectionCommissionMatches, getOverheadVouchers, getToVoucherTypeIds, getVoucherWithInterest, handlePreventNegativeCreditorBalance, handleSavedUpVouchers, isProbablyLastDepositOnAccount, } from './helpers.js';
export const distribute = async (caseVouchers, order, fixedDistributions, options = {}) => {
    const sortedVouchers = caseVouchers
        .sort((a, b) => {
        const fst = a.rawDate - b.rawDate;
        if (fst === 0) {
            if (options.oldFilterByAmountIfDateIsEqual === true) {
                return a.amount - b.amount;
            }
            else {
                return a.id - b.id;
            }
        }
        return fst;
    })
        .map((cv) => {
        const voucherWithDistribution = cv;
        voucherWithDistribution.distribution = [];
        voucherWithDistribution.distributionAmount = 0;
        return voucherWithDistribution;
    });
    const voucherIds = new Set(sortedVouchers.map((v) => v.caseVoucherId));
    const applicableFixedDistributions = fixedDistributions.filter((fd) => voucherIds.has(fd.fromCaseVoucherId) &&
        voucherIds.has(fd.toCaseVoucherId));
    const fromOriginalFds = applicableFixedDistributions.filter((fd) => fd.dateBehavior === FixedDistributionDateBehavior.FromOriginal);
    const fromAppliedFds = applicableFixedDistributions.filter((fd) => fd.dateBehavior === FixedDistributionDateBehavior.FromApplied);
    // Reserve every fixed amount before dynamic allocation. The reservation is
    // replaced by the real distribution when its date behavior takes effect,
    // so timing remains unchanged without allowing earlier dynamic overbooking.
    const reservations = buildFixedDistributionReservations(applicableFixedDistributions);
    if (fromOriginalFds.length !== 0) {
        releaseFixedDistributionReservations(reservations, fromOriginalFds);
        applyFixedDistributions(sortedVouchers, fromOriginalFds);
    }
    const earmarks = options.buildDistributionEarmarks?.(sortedVouchers, reservations) ?? [];
    adjustEarmarkReservations(reservations, earmarks, 1);
    const deferredFdsBySourceVoucherId = mapAggregate(fromAppliedFds, (fd) => fd.fromCaseVoucherId);
    const earmarksBySourceVoucherId = mapAggregate(earmarks, (earmark) => earmark.sourceVoucherId);
    let timeAtLastPause = Date.now();
    let state = [];
    for (let i = 0; i < sortedVouchers.length; i++) {
        const now = Date.now();
        if (now - timeAtLastPause > 4) {
            await yieldTask();
            timeAtLastPause = now;
        }
        const caseVoucher = sortedVouchers[i];
        if (deferredFdsBySourceVoucherId.size > 0) {
            const deferredFdsForVoucher = deferredFdsBySourceVoucherId.get(caseVoucher.caseVoucherId);
            if (deferredFdsForVoucher !== undefined) {
                releaseFixedDistributionReservations(reservations, deferredFdsForVoucher);
                applyFixedDistributions(sortedVouchers, deferredFdsForVoucher);
                deferredFdsBySourceVoucherId.delete(caseVoucher.caseVoucherId);
            }
        }
        const sourceEarmarks = earmarksBySourceVoucherId.get(caseVoucher.caseVoucherId);
        if (sourceEarmarks !== undefined) {
            adjustEarmarkReservations(reservations, sourceEarmarks, -1);
            applyEarmarks(sortedVouchers, sourceEarmarks);
            earmarksBySourceVoucherId.delete(caseVoucher.caseVoucherId);
        }
        const nextState = appendVoucherToState(state, caseVoucher, order, reservations);
        if (options.modifier !== undefined) {
            state = await options.modifier(nextState, state, nextState[i], sortedVouchers[i + 1]);
        }
        else {
            state = nextState;
        }
    }
    return state;
};
const appendVoucherToState = (vouchers, voucher, order, reservations) => {
    vouchers = vouchers.sort((voucherA, voucherB) => order(voucherA, voucherB, voucher));
    for (let i = 0; i < vouchers.length; i++) {
        const from = getUnallocatedBalance(voucher, reservations);
        if (from === 0) {
            break;
        }
        const toVoucher = vouchers[i];
        const to = getUnallocatedBalance(toVoucher, reservations);
        if (to === 0 ||
            // Both is less than 0
            (to > 0 && from > 0) ||
            // Both is greater than 0
            (to < 0 && from < 0)) {
            continue;
        }
        const distributionAmount = to > from ? (to < -from ? -to : from) : -to < from ? -to : from;
        voucher = appendToDistributionOnVoucher(voucher, -distributionAmount, toVoucher.caseVoucherId, toVoucher.date, undefined);
        vouchers[i] = appendToDistributionOnVoucher(vouchers[i], distributionAmount, voucher.caseVoucherId, voucher.date, undefined);
    }
    const v = vouchers.concat(voucher);
    return v;
};
export const getUnallocatedBalance = (voucher, reservations) => {
    const balance = voucher.distributionAmount - voucher.amount;
    const balanceAfterReservations = balance + (reservations.get(voucher.caseVoucherId) ?? 0);
    // Reservations consume capacity; they must not create capacity in the
    // opposite direction while calculated vouchers temporarily have amount 0.
    if (balance < 0) {
        return Math.min(0, balanceAfterReservations);
    }
    if (balance > 0) {
        return Math.max(0, balanceAfterReservations);
    }
    return 0;
};
const buildFixedDistributionReservations = (fixedDistributions) => {
    const reservations = new Map();
    adjustFixedDistributionReservations(reservations, fixedDistributions, 1);
    return reservations;
};
const releaseFixedDistributionReservations = (reservations, fixedDistributions) => {
    adjustFixedDistributionReservations(reservations, fixedDistributions, -1);
};
const changeReservation = (reservations, caseVoucherId, delta) => {
    const nextAmount = (reservations.get(caseVoucherId) ?? 0) + delta;
    if (nextAmount === 0) {
        reservations.delete(caseVoucherId);
    }
    else {
        reservations.set(caseVoucherId, nextAmount);
    }
};
export const adjustEarmarkReservations = (reservations, earmarks, direction) => {
    for (const earmark of earmarks) {
        changeReservation(reservations, earmark.sourceVoucherId, direction * earmark.amount);
        changeReservation(reservations, earmark.targetVoucherId, direction * -earmark.amount);
    }
};
const adjustFixedDistributionReservations = (reservations, fixedDistributions, direction) => {
    for (const fd of fixedDistributions) {
        changeReservation(reservations, fd.fromCaseVoucherId, direction * fd.amount);
        changeReservation(reservations, fd.toCaseVoucherId, direction * -fd.amount);
    }
};
export const appendToDistributionOnVoucher = (voucher, amount, caseVoucherId, date, fixedDistributionId) => {
    if (voucher.distribution != null) {
        voucher.distribution.push({
            caseVoucherId,
            amount,
            date,
            fixedDistributionId,
        });
    }
    else {
        voucher.distribution = [
            {
                caseVoucherId,
                amount,
                date,
                fixedDistributionId,
            },
        ];
    }
    voucher.distributionAmount = (voucher.distributionAmount || 0) + amount;
    return voucher;
};
const applyFixedDistributions = (sortedVouchers, fixedDistributions) => {
    const vouchersById = objectBy(sortedVouchers, (voucher) => voucher.caseVoucherId);
    for (const fd of fixedDistributions) {
        const fromVoucher = vouchersById[fd.fromCaseVoucherId];
        const toVoucher = vouchersById[fd.toCaseVoucherId];
        if (fromVoucher === undefined || toVoucher === undefined) {
            // During incremental interest calculation, distribute() is called on a
            // partial voucher set.  FDs that reference vouchers not yet appended are
            // expected and should be silently skipped.
            continue;
        }
        const date = fd.dateBehavior === FixedDistributionDateBehavior.FromOriginal
            ? toVoucher.date
            : fromVoucher.date;
        appendToDistributionOnVoucher(fromVoucher, fd.amount, toVoucher.caseVoucherId, date, fd.id);
        appendToDistributionOnVoucher(toVoucher, -fd.amount, fromVoucher.caseVoucherId, date, fd.id);
    }
};
const applyEarmarks = (sortedVouchers, earmarks) => {
    const vouchersById = objectBy(sortedVouchers, (voucher) => voucher.caseVoucherId);
    for (const earmark of earmarks) {
        const sourceVoucher = vouchersById[earmark.sourceVoucherId];
        const targetVoucher = vouchersById[earmark.targetVoucherId];
        if (sourceVoucher === undefined || targetVoucher === undefined) {
            continue;
        }
        appendToDistributionOnVoucher(sourceVoucher, earmark.amount, targetVoucher.caseVoucherId, targetVoucher.date, undefined);
        appendToDistributionOnVoucher(targetVoucher, -earmark.amount, sourceVoucher.caseVoucherId, sourceVoucher.date, undefined);
    }
};
export const getShareVouchers = async (baseVoucher, documents, economyPolicy, _case, billingModel, fixedDistributions, conditions, caseVouchers) => {
    if (!billingModel) {
        return [[], [], []];
    }
    if (!billingModel.model.share) {
        throw new Error('Please only use this function for connectors with a share');
    }
    let newBillingVouchers = [];
    let newResidueBillingVouchers = [];
    let newOverheadBillingVouchers = [];
    let savedUpBillingVouchers = [];
    const vouchersWithInterest = await getVoucherWithInterest(billingModel, fixedDistributions, conditions, caseVouchers, documents, economyPolicy);
    if (!vouchersWithInterest) {
        throw new Error('Billing: Could not find vouchers');
    }
    const vouchersWithDistribution = findAndSort(vouchersWithInterest, billingModel.model.from);
    for (let i = 0; i < vouchersWithDistribution.length; i++) {
        const voucherWithDistribution = vouchersWithDistribution[i];
        if (!caseBillingIsActive(voucherWithDistribution.contraCaseVoucher.date, _case)) {
            continue;
        }
        // Check if deposit is excluded from billing
        if (voucherWithDistribution.contraCaseVoucher.details?.excludeFromBilling) {
            continue;
        }
        // Find matches from vouchers
        const shares = getCollectionCommissionMatches(billingModel, _case, voucherWithDistribution.contraCaseVoucher.date, voucherWithDistribution);
        // Convert to billing vouchers
        const shareBaseVoucher = getBaseBillingVoucher(baseVoucher, voucherWithDistribution, billingModel);
        let newCollectionCommissionBillingVouchers = [];
        let shareAmount = 0;
        for (let f = 0; f < shares.length; f++) {
            const share = shares[f];
            const amount = Math.round(voucherWithDistribution.amountFromVoucher * (share.feeFraction || 1));
            shareAmount += amount;
            const toVoucherTypeIds = getToVoucherTypeIds(billingModel, voucherWithDistribution, share);
            // Handle if negative creditor balance is true
            const [_newCollectionCommissionBillingVouchers, savedUpVoucher] = handlePreventNegativeCreditorBalance(billingModel, amount, shareBaseVoucher, toVoucherTypeIds, share);
            savedUpBillingVouchers = savedUpBillingVouchers.concat(savedUpVoucher ? savedUpVoucher : []);
            newCollectionCommissionBillingVouchers =
                newCollectionCommissionBillingVouchers.concat(_newCollectionCommissionBillingVouchers);
        }
        // Generate vouchers which is residue
        const newResidueBillingVoucher = genResidueVouchers(shareAmount, voucherWithDistribution, shareBaseVoucher, billingModel, _case);
        if (newResidueBillingVoucher) {
            newResidueBillingVouchers = newResidueBillingVouchers.concat(newResidueBillingVoucher);
        }
        newBillingVouchers = newBillingVouchers.concat(newCollectionCommissionBillingVouchers);
    }
    // Saved up amount should be paid by residue vouchers
    if (savedUpBillingVouchers.length) {
        const [_newResidueVouchers, _savedUpVouchers, _newVouchers] = handleSavedUpVouchers(newResidueBillingVouchers, savedUpBillingVouchers);
        newResidueBillingVouchers = _newResidueVouchers;
        savedUpBillingVouchers = _savedUpVouchers;
        newBillingVouchers = newBillingVouchers.concat(_newVouchers);
    }
    // If this is the last deposit
    // TODO: Store saved up vouchers in database and handle this with billing appendices instead
    if (savedUpBillingVouchers.length &&
        caseVouchers &&
        vouchersWithDistribution[vouchersWithDistribution.length - 1] &&
        (await isProbablyLastDepositOnAccount(caseVouchers, documents, economyPolicy, vouchersWithDistribution[vouchersWithDistribution.length - 1]
            .contraCaseVoucher.date, fixedDistributions, conditions))) {
        // Consider making a new voucher type for this
        newBillingVouchers = newBillingVouchers.concat(savedUpBillingVouchers.map((savedUpVoucher) => ({
            ...savedUpVoucher,
            depositCaseVoucherId: null,
            billingModelId: null,
            details: {
                ...savedUpVoucher.details,
                depositVoucherTypeId: null,
            },
        })));
        savedUpBillingVouchers = [];
    }
    // Force billing of collection commission
    if (caseVouchers) {
        const newForceBillingVouchers = await forceCollectionCommissionBilling(_case, billingModel, newBillingVouchers, caseVouchers, baseVoucher, documents, economyPolicy, fixedDistributions, conditions);
        newBillingVouchers = newBillingVouchers.concat(newForceBillingVouchers);
    }
    // Overhead
    if (!vouchersWithInterest) {
        throw new Error('Billing: Could not find vouchers');
    }
    newOverheadBillingVouchers = getOverheadVouchers(baseVoucher, vouchersWithInterest, _case, billingModel);
    return [
        newBillingVouchers,
        newResidueBillingVouchers,
        newOverheadBillingVouchers,
    ];
};
//# sourceMappingURL=index.js.map